import { ASSET_CATEGORY_NAMES, ASSET_CATEGORY_SLUG_ORDER } from "enums/AssetCategoryEnum";
import { AssetCategorySlug } from "enums/AssetCategoryEnum";
import { GroupedInvestmentsData } from "./types";

export const openMeetingBooking = () => {
  const w = window.open(
    "https://reliance.pro/inwestycje/#konsultacja-inwestycje",
    "_blank",
    "noopener,noreferrer",
  );
  if (w) w.opener = null;
};

const DETAILS_FIELDS: Record<string, string[]> = {
  real_estate: ["propertyType", "propertyTypeOther", "area", "city", "address"],
  precious_stones: ["weight", "cut", "color", "clarity"],
  precious_metals: ["propertyType", "weight", "fineness"],
  treasury_bonds: ["interestType"],
  artwork: ["artworkType", "author", "eraAge"],
  investment_policy: ["contractDate", "durationDate", "premiumPayment"],
  loans: ["loanType", "entity", "interestRate", "interestRateType", "interestPayment", "autoRenewal", "contractDate", "durationDate"],
  crypto: ["parentInvestmentUuid"],
};

const NUMBER_FIELDS = ["primaryValue", "quantity", "purchaseRate"];

const NULLABLE_FIELDS: Record<string, string[]> = {
  investment_policy: ["durationDate"],
  loans: ["durationDate"],
};

export const buildInvestmentPayload = (
  values: Record<string, unknown>,
  slug: string,
) => {
  const detailsFields = DETAILS_FIELDS[slug] || [];
  const nullableFields = NULLABLE_FIELDS[slug] || [];
  const details: Record<string, unknown> = {};
  const root: Record<string, unknown> = {};

  for (const [key, val] of Object.entries(values)) {
    if (key.startsWith("_")) continue;
    if ((val === "" || val === null || val === undefined) && !nullableFields.includes(key)) continue;

    if (detailsFields.includes(key)) {
      details[key] = NUMBER_FIELDS.includes(key) ? Number(val) : val;
    } else {
      root[key] = NUMBER_FIELDS.includes(key) ? Number(val) : val;
    }
  }

  return {
    ...root,
    ...(Object.keys(details).length > 0 ? { details } : {}),
  };
};

const ASSET_MODAL_TITLES: Record<
  string,
  { user: { add: string; edit: string }; admin: { add: string; edit: string } }
> = {
  treasury_bonds: {
    user: {
      add: "Dodaj obligacje skarbowe zakupione poza Reliance",
      edit: "Edytuj obligacje skarbowe zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj obligacje skarbowe",
      edit: "Edytuj obligacje skarbowe",
    },
  },
  currencies: {
    user: {
      add: "Dodaj obce waluty zakupione poza Reliance",
      edit: "Edytuj obce waluty zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj obce waluty",
      edit: "Edytuj obce waluty",
    },
  },
  loans: {
    user: {
      add: "Dodaj pożyczki zaciągnięte poza Reliance",
      edit: "Edytuj pożyczki zaciągnięte poza Reliance",
    },
    admin: {
      add: "Dodaj pożyczki",
      edit: "Edytuj pożyczki",
    },
  },
  crypto: {
    user: {
      add: "Dodaj tokeny / kryptowaluty zakupione poza Reliance",
      edit: "Edytuj tokeny / kryptowaluty zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj tokeny / kryptowaluty",
      edit: "Edytuj tokeny / kryptowaluty",
    },
  },
  investment_policy: {
    user: {
      add: "Dodaj polisy inwestycyjne / IKE zakupione poza Reliance",
      edit: "Edytuj polisy inwestycyjne / IKE zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj polisy inwestycyjne / IKE",
      edit: "Edytuj polisy inwestycyjne / IKE",
    },
  },
  precious_stones: {
    user: {
      add: "Dodaj kamienie szlachetne zakupione poza Reliance",
      edit: "Edytuj kamienie szlachetne zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj kamienie szlachetne",
      edit: "Edytuj kamienie szlachetne",
    },
  },
  precious_metals: {
    user: {
      add: "Dodaj metale szlachetne zakupione poza Reliance",
      edit: "Edytuj metale szlachetne zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj metale szlachetne",
      edit: "Edytuj metale szlachetne",
    },
  },
  artwork: {
    user: {
      add: "Dodaj dzieła sztuki zakupione poza Reliance",
      edit: "Edytuj dzieła sztuki zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj dzieła sztuki",
      edit: "Edytuj dzieła sztuki",
    },
  },
  real_estate: {
    user: {
      add: "Dodaj nieruchomość zakupioną poza Reliance",
      edit: "Edytuj nieruchomość zakupioną poza Reliance",
    },
    admin: {
      add: "Dodaj nieruchomość",
      edit: "Edytuj nieruchomość",
    },
  },
  stocks: {
    user: {
      add: "Dodaj akcje zakupione poza Reliance",
      edit: "Edytuj akcje zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj akcje",
      edit: "Edytuj akcje",
    },
  },
  shares: {
    user: {
      add: "Dodaj udziały zakupione poza Reliance",
      edit: "Edytuj udziały zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj udziały",
      edit: "Edytuj udziały",
    },
  },
  other_investments: {
    user: {
      add: "Inne aktywa zakupione poza Reliance",
      edit: "Edytuj inne aktywa zakupione poza Reliance",
    },
    admin: {
      add: "Dodaj inne aktywa",
      edit: "Edytuj inne aktywa",
    },
  },
};

export const getAssetModalTitle = (
  slug: string | null,
  isAdmin: boolean,
  mode: "add" | "edit" = "add",
): string => {
  if (!slug) return "";
  const role = isAdmin ? "admin" : "user";
  const titles = ASSET_MODAL_TITLES[slug];
  if (!titles) return isAdmin ? "Dodaj aktywo" : "Dodaj aktywo zakupione poza Reliance";
  return titles[role][mode];
};


export type AdminFilterCategories = GroupedInvestmentsData["reliance"];

export const sortCategoriesBySlugOrder = (
  categories: AdminFilterCategories | undefined,
): AdminFilterCategories | undefined => {
  if (!categories) return categories;
  const sorted = Object.entries(categories).sort(([, a], [, b]) => {
    const aIdx = ASSET_CATEGORY_SLUG_ORDER.indexOf(a.investmentType?.slug);
    const bIdx = ASSET_CATEGORY_SLUG_ORDER.indexOf(b.investmentType?.slug);
    const aOrder = aIdx === -1 ? ASSET_CATEGORY_SLUG_ORDER.length : aIdx;
    const bOrder = bIdx === -1 ? ASSET_CATEGORY_SLUG_ORDER.length : bIdx;
    return aOrder - bOrder;
  });
  const result: AdminFilterCategories = {};
  sorted.forEach(([key, category], index) => {
    result[key] = { ...category, order: index };
  });
  return result;
};

export const itemMatchesClient = (item: any, clientValue: any): boolean => {
  if (Array.isArray(item?.clients) && item.clients.length > 0) {
    return item.clients.some((c: any) => c?.uuid === clientValue);
  }
  if (item?.owner?.uuid) {
    return item.owner.uuid === clientValue;
  }
  if (Array.isArray(item?.shares)) {
    return item.shares.some((s: any) => s?.user?.uuid === clientValue);
  }
  return false;
};

export const filterCategoriesByClient = (
  categories: AdminFilterCategories | undefined,
  clientValue: any,
): AdminFilterCategories | undefined => {
  if (!categories || !clientValue) return categories;
  const result: AdminFilterCategories = {};
  for (const [key, category] of Object.entries(categories)) {
    const filteredItems = (category.items ?? []).filter((item: any) =>
      itemMatchesClient(item, clientValue),
    );
    if (filteredItems.length > 0) {
      result[key] = { ...category, items: filteredItems };
    }
  }
  return result;
};

export const filterCategoriesByType = (
  categories: AdminFilterCategories | undefined,
  typeSlug: string | null,
): AdminFilterCategories | undefined => {
  if (!categories || !typeSlug) return categories;
  const result: AdminFilterCategories = {};
  for (const [key, category] of Object.entries(categories)) {
    if (category.investmentType?.slug === typeSlug) {
      result[key] = category;
    }
  }
  return result;
};

export const getTypeBadges = (
  categories: AdminFilterCategories | undefined,
): { slug: string; name: string }[] => {
  if (!categories) return [];
  return Object.values(categories)
    .map((cat) => {
      const slug = cat.investmentType?.slug as AssetCategorySlug;
      return {
        slug,
        name: ASSET_CATEGORY_NAMES[slug] ?? cat.investmentType?.name,
      };
    })
    .sort((a, b) => {
      const aIdx = ASSET_CATEGORY_SLUG_ORDER.indexOf(a.slug);
      const bIdx = ASSET_CATEGORY_SLUG_ORDER.indexOf(b.slug);
      const aOrder = aIdx === -1 ? ASSET_CATEGORY_SLUG_ORDER.length : aIdx;
      const bOrder = bIdx === -1 ? ASSET_CATEGORY_SLUG_ORDER.length : bIdx;
      return aOrder - bOrder;
    });
};

export const getClientUuidsForType = (
  categories: AdminFilterCategories | undefined,
  typeSlug: string | null,
): Set<string> | null => {
  if (!categories || !typeSlug) return null;
  const uuids = new Set<string>();
  for (const category of Object.values(categories)) {
    if (category.investmentType?.slug !== typeSlug) continue;
    for (const item of category.items ?? []) {
      const anyItem = item as any;
      if (Array.isArray(anyItem?.clients) && anyItem.clients.length > 0) {
        anyItem.clients.forEach((c: any) => {
          if (c?.uuid) uuids.add(c.uuid);
        });
      } else if (anyItem?.owner?.uuid) {
        uuids.add(anyItem.owner.uuid);
      } else if (Array.isArray(anyItem?.shares)) {
        anyItem.shares.forEach((s: any) => {
          if (s?.user?.uuid) uuids.add(s.user.uuid);
        });
      }
    }
  }
  return uuids;
};
